/**
 * Agent recommendations: recommend events to students.
 */

import { supabase } from '../lib/supabase'
import {
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

/**
 * Get recommended events for a student (from agent_recommendations + optional featured/public).
 * Returns events with recommendation metadata (note, is_featured, expires_at).
 * @param {string} studentId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getStudentRecommendations = async (studentId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own recommendations.')
  }

  const now = new Date().toISOString()
  const { data, error } = await supabase
    .from('agent_recommendations')
    .select(`
      id,
      event_id,
      recommendation_note,
      is_featured,
      expires_at,
      created_at,
      event:events(id, title, description, type, status, image_url, featured_image_url, event_slug, created_at)
    `)
    .eq('student_id', studentId)
    .or(`expires_at.is.null,expires_at.gte.${now}`)
    .order('is_featured', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to load recommendations.') }

  const list = (data ?? []).filter((r) => r.event && r.event.status === 'public')
  return { data: list, error: null }
}

/**
 * Create a recommendation (agent recommends an event to a student).
 */
export const createRecommendation = async (
  agentId,
  studentId,
  eventId,
  note = null,
  options = {}
) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student') {
    return accessDeniedResponse('Only agents can create recommendations.')
  }

  if (roleContext.role === 'agent' && roleContext.userId !== agentId) {
    return accessDeniedResponse('You can only create recommendations as yourself.')
  }

  const access = await ensureCanManageEvent(eventId)
  if (!access.allowed) return { data: null, error: access.error }

  const { data, error } = await supabase
    .from('agent_recommendations')
    .upsert(
      {
        agent_id: agentId,
        student_id: studentId,
        event_id: eventId,
        recommendation_note: note ?? null,
        is_featured: false
      },
      { onConflict: 'agent_id,student_id,event_id' }
    )
    .select('*')
    .single()

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to add recommendation.') }
  return { data, error: null }
}

/**
 * Delete a recommendation.
 */
export const deleteRecommendation = async (recommendationId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student') {
    return accessDeniedResponse('Only agents can remove recommendations.')
  }

  const { data, error } = await supabase
    .from('agent_recommendations')
    .delete()
    .eq('id', recommendationId)
    .select()
    .single()

  return handleQuery(
    Promise.resolve({ data, error }),
    { fallbackMessage: 'Unable to remove recommendation.', allowEmpty: false }
  )
}
